// app/src/trading-sessions/SessionChart.tsx
import { useState, useRef, useMemo, useEffect } from "react";
import { getHistoricalPriceData, useQuery } from "wasp/client/operations";
import { Card, CardContent } from "../client/components/ui/card";
import useTranslation from "../client/hooks/useTranslation";
import { useChartEngine } from "../dashboard/widgets/chart/useChartEngine";
import { useTradeMarkers } from "../dashboard/widgets/chart/useTradeMarkers";
import ChartErrorBoundary from "../dashboard/widgets/chart/ChartErrorBoundary";
import type { Timeframe } from "../dashboard/widgets/chart/chartTypes";

const TIMEFRAMES: Timeframe[] = ["1m", "5m", "15m", "30m", "1h", "4h", "1d"];

const INTERVAL_MS: Record<Timeframe, number> = {
  "1m": 60_000, "5m": 300_000, "15m": 900_000, "30m": 1_800_000,
  "1h": 3_600_000, "4h": 14_400_000, "1d": 86_400_000, "1w": 604_800_000,
};

// Yahoo Finance only serves intraday candles for a limited recent window per
// interval (1m: 30d, 5m/15m/30m: 60d, 1h: 730d, 1d+: illimité). Picking a
// finer interval than Yahoo retains for the session's age returns a 404 that
// the chart used to surface as "Symbole invalide". For old sessions we clamp
// to the finest interval still available.
const TIMEFRAME_MAX_AGE_DAYS: Record<Timeframe, number> = {
  "1m": 30,
  "5m": 60, "15m": 60, "30m": 60,
  "1h": 730, "4h": 730,
  "1d": Infinity, "1w": Infinity,
};

// Coarsest "minimum" timeframe Yahoo still serves for a session this many days old.
function minAvailableTimeframe(daysAgo: number): Timeframe {
  if (daysAgo <= TIMEFRAME_MAX_AGE_DAYS["1m"]) return "1m";
  if (daysAgo <= TIMEFRAME_MAX_AGE_DAYS["5m"]) return "5m";
  if (daysAgo <= TIMEFRAME_MAX_AGE_DAYS["1h"]) return "1h";
  return "1d";
}

const TIMEFRAME_ORDER: Timeframe[] = ["1m", "5m", "15m", "30m", "1h", "4h", "1d", "1w"];

function autoTimeframe(startedAt: Date, endedAt: Date): Timeframe {
  const durationMs = endedAt.getTime() - startedAt.getTime();
  const durationH = durationMs / 3_600_000;
  let tf: Timeframe;
  if (durationH < 1.5) tf = "1m";
  else if (durationH < 6) tf = "5m";
  else if (durationH < 12) tf = "15m";
  else if (durationH < 24) tf = "30m";
  else tf = "1h";

  // Clamp to what Yahoo still retains for this session's age, so old sessions
  // don't default to a 1m chart that Yahoo refuses to serve.
  const daysAgo = (Date.now() - endedAt.getTime()) / 86_400_000;
  const floor = minAvailableTimeframe(daysAgo);
  if (TIMEFRAME_ORDER.indexOf(tf) < TIMEFRAME_ORDER.indexOf(floor)) tf = floor;
  return tf;
}

interface SessionChartProps {
  session: { startedAt: Date | string | null; endedAt: Date | string | null };
  trades: any[];
  accounts: any[];
  isDark: boolean;
  /**
   * Fill the parent's height instead of using the fixed 480px chart area.
   * Used when embedded in a widget card (Last Session) so the chart grows with
   * the available space; the standalone detail page leaves it off (default).
   */
  fill?: boolean;
}

export function SessionChart({ session, trades, accounts, isDark, fill = false }: SessionChartProps) {
  const t = useTranslation();

  const startedAt = session.startedAt ? new Date(session.startedAt) : null;
  const endedAt = session.endedAt ? new Date(session.endedAt) : null;
  const valid = !!startedAt && !!endedAt && trades.length > 0;

  // ALL hooks must run unconditionally before any early return
  const defaultTimeframe: Timeframe = (startedAt && endedAt)
    ? autoTimeframe(startedAt, endedAt)
    : "15m";
  const [timeframe, setTimeframe] = useState<Timeframe>(defaultTimeframe);

  // The session window can widen AFTER the first render (trades load async, and
  // a late-closing trade pushes endedAt out — e.g. a short covered hours later).
  // Re-derive the auto timeframe when the window changes, unless the user has
  // manually picked one. Without this, the chart stays stuck on the timeframe
  // computed from the initial (often near-zero) window.
  const userPickedTimeframe = useRef(false);
  const windowKey = `${startedAt?.getTime() ?? 0}-${endedAt?.getTime() ?? 0}`;
  const prevWindowKey = useRef(windowKey);
  if (prevWindowKey.current !== windowKey) {
    prevWindowKey.current = windowKey;
    if (!userPickedTimeframe.current) setTimeframe(defaultTimeframe);
  }

  // Normalize futures symbols to root: MNQM6 → MNQ, ESZ25 → ES, NQ1! → NQ
  function normalizeRoot(sym: string): string {
    const s = sym.toUpperCase();
    if (s.endsWith("!")) return s.replace(/\d+!$/, "");
    const m = s.match(/^([A-Z]{1,6})[FGHJKMNQUVXZ]\d{1,2}$/);
    return m ? m[1] : s;
  }

  // Group trades by normalized root symbol, keep one raw symbol per group for the query
  const symbolGroups = useMemo(() => {
    const groups: Record<string, { count: number; rawSymbol: string }> = {};
    for (const tr of trades) {
      if (!tr.symbol) continue;
      const root = normalizeRoot(tr.symbol);
      if (!groups[root]) groups[root] = { count: 0, rawSymbol: tr.symbol };
      groups[root].count++;
    }
    return groups;
  }, [trades]);

  const symbols = useMemo(
    () => Object.keys(symbolGroups).sort((a, b) => symbolGroups[b].count - symbolGroups[a].count),
    [symbolGroups]
  );

  const [activeSymbol, setActiveSymbol] = useState<string>("");

  // Sync activeSymbol when symbols list changes (e.g. after trades load)
  useEffect(() => {
    if (symbols.length > 0 && !symbols.includes(activeSymbol)) {
      setActiveSymbol(symbols[0]);
    }
  }, [symbols]);

  const activeAccountIds = useMemo(
    () => new Set(trades.map((tr: any) => tr.accountId).filter(Boolean)),
    [trades]
  );
  const activeAccounts = useMemo(
    () => accounts.filter((a: any) => activeAccountIds.has(a.id)),
    [accounts, activeAccountIds]
  );

  // Key derived from active account IDs — used to reset selection when accounts change
  const accountKey = activeAccounts.map((a: any) => a.id).sort().join(",");
  const [selectedAccountIds, setSelectedAccountIds] = useState<Set<string>>(
    () => new Set(activeAccounts.map((a: any) => a.id))
  );
  const prevAccountKeyRef = useRef(accountKey);
  if (prevAccountKeyRef.current !== accountKey) {
    prevAccountKeyRef.current = accountKey;
    setSelectedAccountIds(new Set(activeAccounts.map((a: any) => a.id)));
  }

  const toggleAccount = (id: string) => {
    setSelectedAccountIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const { fromDate, toDate } = useMemo(() => {
    if (!startedAt || !endedAt) return { fromDate: new Date(), toDate: new Date() };
    const pad = 10 * INTERVAL_MS[timeframe];
    return {
      fromDate: new Date(startedAt.getTime() - pad),
      toDate: new Date(endedAt.getTime() + 20 * INTERVAL_MS[timeframe]),
    };
  }, [startedAt?.getTime(), endedAt?.getTime(), timeframe]);

  // Use the raw symbol for the query (server handles normalization to Yahoo format)
  const querySymbol = symbolGroups[activeSymbol]?.rawSymbol ?? activeSymbol;

  const { data: priceData, isLoading, error } = useQuery(
    getHistoricalPriceData,
    { symbol: querySymbol, date: fromDate, toDate, interval: timeframe },
    { enabled: valid && !!activeSymbol }
  );

  const pricePrecision = useMemo(() => {
    if (!priceData || priceData.length === 0) return 5;
    const firstOpen = Number(priceData[0].open);
    return isNaN(firstOpen) ? 5 : firstOpen < 10 ? 5 : 2;
  }, [priceData]);

  const filteredTrades = useMemo(
    () => trades.filter((tr: any) =>
      selectedAccountIds.has(tr.accountId ?? "") &&
      normalizeRoot(tr.symbol ?? "") === activeSymbol
    ),
    [trades, selectedAccountIds, activeSymbol]
  );

  const containerRef = useRef<HTMLDivElement>(null);
  const { seriesRef, chartRef, seriesVersion } = useChartEngine(containerRef, valid ? (priceData ?? []) : [], {
    isDark,
    pricePrecision,
  });

  // Zoom to session window after data loads
  useEffect(() => {
    if (!chartRef.current || !priceData?.length || !startedAt || !endedAt) return;
    chartRef.current.timeScale().setVisibleRange({
      from: Math.floor(startedAt.getTime() / 1000) as any,
      to: Math.floor(endedAt.getTime() / 1000) as any,
    });
  }, [priceData, startedAt?.getTime(), endedAt?.getTime()]);

  const candleTimes = useMemo(
    () => (priceData ?? []).map((d: any) => d.time as number).sort((a: number, b: number) => a - b),
    [priceData]
  );

  // showExit=true: render exit markers too, so every closed trade shows both
  // its entry AND exit on the session chart (a session has many trades, and
  // long exits / short exits were previously invisible).
  useTradeMarkers(seriesRef, filteredTrades, activeSymbol, true, candleTimes, seriesVersion, true);

  // Guard: render nothing if session has no valid timing or no trades
  if (!valid) return null;

  return (
    <Card className={fill ? "h-full flex flex-col" : undefined}>
      <CardContent className={fill ? "p-0 flex-1 flex flex-col min-h-0" : "p-0"}>
        <div className="px-4 pt-3 pb-0">
          <p className="text-[10px] font-semibold tracking-widest uppercase text-primary/80">
            {t.sessions.chartTitle}
          </p>
        </div>

        <div className="flex items-center gap-2 px-4 py-2 flex-wrap">
          {/* Symbol tabs */}
          <div className="flex gap-1">
            {symbols.map((sym) => (
              <button
                key={sym}
                onClick={() => setActiveSymbol(sym)}
                className={`text-[11px] font-mono px-2 py-0.5 rounded transition-colors ${
                  sym === activeSymbol
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground hover:bg-accent/60"
                }`}
              >
                {sym}
              </button>
            ))}
          </div>

          {/* Timeframe pills */}
          <div className="flex gap-0.5">
            {TIMEFRAMES.map((tf) => (
              <button
                key={tf}
                onClick={() => { userPickedTimeframe.current = true; setTimeframe(tf); }}
                className={`text-[10px] px-1.5 py-0.5 rounded transition-colors ${
                  tf === timeframe
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground hover:bg-accent/60"
                }`}
              >
                {tf}
              </button>
            ))}
          </div>

          {/* Account checkboxes */}
          <div className="flex items-center gap-2 ml-auto flex-wrap">
            {activeAccounts.map((account: any) => {
              const color = account.effectiveTemplate?.color ?? "hsl(var(--muted-foreground))";
              const checked = selectedAccountIds.has(account.id);
              return (
                <button
                  key={account.id}
                  onClick={() => toggleAccount(account.id)}
                  className={`flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded transition-colors ${
                    checked ? "bg-accent/40 text-foreground" : "text-muted-foreground/50"
                  }`}
                >
                  <span
                    className="w-2 h-2 rounded-full shrink-0"
                    style={{ background: checked ? color : "hsl(var(--muted-foreground))" }}
                  />
                  {account.name}
                </button>
              );
            })}
          </div>
        </div>

        <div className={`relative ${fill ? "flex-1 min-h-0" : "h-[480px]"}`}>
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-5 h-5 rounded-full border-2 border-primary border-t-transparent animate-spin" />
            </div>
          )}
          {!!error && !isLoading && (
            <div className="absolute inset-0 flex items-center justify-center text-destructive text-sm">
              {t.chart.invalidSymbol}
            </div>
          )}
          {!isLoading && !error && priceData?.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center text-muted-foreground text-sm">
              {t.chart.noData}
            </div>
          )}
          <ChartErrorBoundary>
            <div ref={containerRef} className="w-full h-full" />
          </ChartErrorBoundary>
          <span className="absolute bottom-2 left-2 text-[9px] font-mono text-muted-foreground/60 pointer-events-none select-none">
            Heure : Europe/Paris
          </span>
        </div>
      </CardContent>
    </Card>
  );
}
